import React, { useState, cloneElement } from "react";
import styles from "./Auth.module.css";
import InputField from "./InputField";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
// import Button from 'react-bootstrap/Button'; // Removed to use native button
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { signUp } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import aws_exports from "../../aws-exports";
import { ToastContainer, toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

Amplify.configure(aws_exports);

function Register() {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        company: "",
        jobTitle: "",
        email: "",
        password: "",
        reenterPassword: "",
        phoneNumber: "",
        university: "",
    });
    const [tosAgreement, setTosAgreement] = useState(false);
    const [privacyAgreement, setPrivacyAgreement] = useState(false);
    const [eye, setEye] = useState(false); // For handling password visibility

    const navigate = useNavigate();

    function handleInputChange(field, value) {
        setFormData((prevData) => ({
            ...prevData,
            [field]: value,
        }));
    };
    
    function eyeClick() {
        setEye((prev) => !prev)
    }

    async function handleSubmit(e) {
        e.preventDefault();

        const { firstName, lastName, company, jobTitle, email, password, reenterPassword, phoneNumber, university } = formData;

        if (!firstName || !lastName || !email || !password) {
            toast.error("Please fill out all fields.");
            return;
        } else if (!tosAgreement || !privacyAgreement) {
            toast.error("You must agree to both the Terms of Service and Privacy Policy to proceed.");
            return;
        } else if(phoneNumber && !isPossiblePhoneNumber(phoneNumber, "US")) {
            toast.error("Please, enter a 10 digit phone number.");
            return;
        } else if(!email.toLowerCase().match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
                toast.error("Please, enter an appropriate email format: email@address.com.");
                return;
        }

        if (password !== reenterPassword) {
            toast.error("Passwords do not match.");
            return;
        }

        try {
            // Signing up a user using AWS Amplify
            const signUpResponse = await signUp({
                username: email,
                password,
                options: {
                  // Company, job title and phone are optional (free tier signups
                  // are often students/hobbyists); omit empty ones rather than
                  // sending "" — Cognito rejects an empty phone_number.
                  userAttributes: {
                    email,
                    given_name: firstName,
                    family_name: lastName,
                    ...(phoneNumber && { phone_number: phoneNumber }),
                    ...(company && { "custom:company": company }),
                    ...(jobTitle && { "custom:job_title": jobTitle }),
                    ...(university && { "custom:university": university }),
                  },
                },
            });

            navigate("/auth/login?source=registered");
        } catch (error) {
            console.error("Sign up error: ", error);
            toast.error(error.message || "An error occurred during registration.");
        }
    }

    return (
        <div className={styles["registeration-content"]}>
            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={true}
                closeButton={true}
            />

            <div className={styles["auth-form-header"]} style={{ marginBottom: '15px' }}>
                <h5 style={{ marginBottom: '8px' }}>Create an Account</h5>

                <p className={styles["switch-auth"]}>
                    Already have an account? <a href="/auth/login" className={styles["txt-link"]}>Login</a>
                </p>
            </div>

            <form className={styles["register-form"]} onSubmit={handleSubmit}>
                <div className={styles["form-name-row"]}>
                    <InputField 
                        desc="First name*"
                        type="text"
                        name="firstName"
                        placeholder="Enter your first name"
                        value={formData.firstName}
                        onChange={(value) => handleInputChange("firstName", value)}
                    />

                    <InputField 
                        desc="Last name*"
                        type="text"
                        name="lastName"
                        placeholder="Enter your last name"
                        value={formData.lastName}
                        onChange={(value) => handleInputChange("lastName", value)}
                    />
                </div>

                {/* Company and Job Title on same line */}
                <div className={styles["input"]}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <p className={styles["input-desc"]}>Company</p>
                            <input 
                                type="text"
                                className={styles["input-field"]}
                                name="company"
                                placeholder="Company name (optional)"
                                value={formData.company}
                                onChange={(e) => handleInputChange("company", e.target.value)}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <p className={styles["input-desc"]}>Job Title</p>
                            <input 
                                type="text"
                                className={styles["input-field"]}
                                name="jobTitle"
                                placeholder="Job title (optional)"
                                value={formData.jobTitle}
                                onChange={(e) => handleInputChange("jobTitle", e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Email and Phone on same row */}
                <div className={styles["input"]}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <p className={styles["input-desc"]}>Email address*</p>
                            <input
                                type="email"
                                className={styles["input-field"]}
                                name="email"
                                placeholder="Email address"
                                value={formData.email}
                                onChange={(e) => handleInputChange("email", e.target.value)}
                            />
                        </div>
                        <div style={{ flex: 1 }} className={styles["phone-input"]}>
                            <p className={styles["input-desc"]}>Phone number</p>
                            <PhoneInput
                                placeholder="Phone number (optional)"
                                countryCallingCodeEditable={false}
                                defaultCountry="US"
                                value={formData.phoneNumber}
                                onChange={(value) => handleInputChange("phoneNumber", value)}
                                className={styles["input-field"]}
                            />
                        </div>
                    </div>
                </div>

                <InputField
                    desc="University affiliation"
                    type="text"
                    name="university"
                    placeholder="Enter your university (optional)"
                    value={formData.university}
                    onChange={(value) => handleInputChange("university", value)}
                />

                {/* Password fields on same row */}
                <div className={styles["input"]}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }} className={styles["password-input"]}>
                            <p className={styles["input-desc"]}>Password*</p>
                            <div className={styles["pwd"]}>
                                <input
                                    type={eye ? "text" : "password"}
                                    className={styles["input-field"]}
                                    name="password"
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={(e) => handleInputChange("password", e.target.value)}
                                />
                                {eye ?
                                    <VisibilityIcon className={styles["viz-icon"]} onClick={eyeClick} />
                                    :
                                    <VisibilityOffIcon className={styles["viz-icon"]} onClick={eyeClick} />
                                }
                            </div>
                        </div>
                        <div style={{ flex: 1 }} className={styles["password-input"]}>
                            <p className={styles["input-desc"]}>Confirm password*</p>
                            <div className={styles["pwd"]}>
                                <input
                                    type={eye ? "text" : "password"}
                                    className={styles["input-field"]}
                                    name="reenterPassword"
                                    placeholder="Confirm password"
                                    value={formData.reenterPassword}
                                    onChange={(e) => handleInputChange("reenterPassword", e.target.value)}
                                />
                                {eye ?
                                    <VisibilityIcon className={styles["viz-icon"]} onClick={eyeClick} />
                                    :
                                    <VisibilityOffIcon className={styles["viz-icon"]} onClick={eyeClick} />
                                }
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles["auth-form-footer"]}>
                    <div className={styles["legal-agreements"]}>
                        <div className={styles["legal-agreement"]}>
                            <label className={styles["checkbox-label"]}>
                                <input
                                    type="checkbox"
                                    checked={tosAgreement}
                                    onChange={(e) => setTosAgreement(e.target.checked)}
                                    className={styles["legal-checkbox"]}
                                />
                                <span className={styles["checkmark"]}></span>
                                I agree to the <a href="/tos" className={styles["txt-link"]} target="_blank" rel="noopener noreferrer">Terms of Service</a>*
                            </label>
                        </div>
                        <div className={styles["legal-agreement"]}>
                            <label className={styles["checkbox-label"]}>
                                <input
                                    type="checkbox"
                                    checked={privacyAgreement}
                                    onChange={(e) => setPrivacyAgreement(e.target.checked)}
                                    className={styles["legal-checkbox"]}
                                />
                                <span className={styles["checkmark"]}></span>
                                I agree to the <a href="/privacy-policy" className={styles["txt-link"]} target="_blank" rel="noopener noreferrer">Privacy Policy</a>*
                            </label>
                        </div>
                    </div>

                    <button 
                        className={`${styles["btn-dark"]}`} 
                        type="submit"
                        disabled={!tosAgreement || !privacyAgreement}
                    >
                        Submit
                        <ArrowForwardIcon className={styles["btn-arrow"]}/>
                    </button>
                </div>         
            </form>
        </div>
    )
}

export default Register;
